import { useEffect, useRef } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { MessageSquare, Zap, Shield, BarChart3, ExternalLink } from 'lucide-react';
import SetupWizard from './pages/SetupWizard';
import SetupError from './pages/SetupError';
import SetupComplete from './pages/SetupComplete';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Support from './pages/Support';
import Features from './pages/Features';
import Header from './components/Header';
import Footer from './components/Footer';
import SEOHead from './components/SEOHead';
import { PAGE_SEO } from './seo';

// AWS Marketplace listing URL - update this when the listing is published
const MARKETPLACE_URL = import.meta.env.VITE_MARKETPLACE_URL || 'https://aws.amazon.com/marketplace/search/results?searchTerms=PartnerBuddy';

function App() {
  return (
    <div className="min-h-screen">
      <Routes>
        <Route path="/setup/:tenantId" element={<SetupWizard />} />
        <Route path="/setup/:tenantId/:step" element={<SetupWizard />} />
        <Route path="/setup/error" element={<SetupError />} />
        <Route path="/setup/complete" element={<SetupComplete />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/support" element={<Support />} />
        <Route path="/features" element={<Features />} />
        <Route path="*" element={<Landing />} />
      </Routes>
    </div>
  );
}

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-scrollReveal');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    const children = el.querySelectorAll('.scroll-reveal');
    children.forEach((child) => observer.observe(child));

    return () => observer.disconnect();
  }, []);

  return ref;
}

function Landing() {
  // Check for dev mode via query param
  const params = new URLSearchParams(window.location.search);
  const devMode = params.get('dev') === 'true';
  const tenantId = params.get('tenant') || 'T1GD9KKRA';

  const featuresRef = useScrollReveal();
  const stepsRef = useScrollReveal();

  if (devMode) {
    window.location.href = `/setup/${tenantId}`;
    return null;
  }

  return (
    <div className="min-h-screen">
      <SEOHead page={PAGE_SEO.home} />
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-white to-pb-gray-50 py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-display font-bold text-pb-blue mb-6">
            Manage AWS Partner Opportunities
            <br />
            <span className="text-pb-orange">Where You Work</span>
          </h1>
          <p className="text-xl text-pb-gray-600 mb-4 max-w-2xl mx-auto">
            PartnerBuddy brings your AWS ACE opportunities to Slack and Microsoft Teams with
            AI-powered natural language queries, CRM sync, and automated reporting —{' '}
            <span className="font-semibold text-pb-blue">for just $100/month</span>.*
          </p>
          <div className="space-y-4 mb-8">
            {/* Messaging Platforms */}
            <div className="flex items-center justify-center gap-2">
              <span className="text-pb-gray-500 text-sm">Works with:</span>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-pb-gray-700">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.124 2.521a2.528 2.528 0 0 1 2.52-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.52V8.834zm-1.271 0a2.528 2.528 0 0 1-2.521 2.521 2.528 2.528 0 0 1-2.521-2.521V2.522A2.528 2.528 0 0 1 15.166 0a2.528 2.528 0 0 1 2.521 2.522v6.312zm-2.521 10.124a2.528 2.528 0 0 1 2.521 2.52A2.528 2.528 0 0 1 15.166 24a2.528 2.528 0 0 1-2.521-2.522v-2.52h2.521zm0-1.271a2.528 2.528 0 0 1-2.521-2.521 2.528 2.528 0 0 1 2.521-2.521h6.312A2.528 2.528 0 0 1 24 15.166a2.528 2.528 0 0 1-2.522 2.521h-6.312z"/>
                  </svg>
                  <span className="font-medium">Slack</span>
                </div>
                <div className="flex items-center gap-2 text-pb-gray-700">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.214 0L0 11.214v1.572L11.214 24h1.572L24 12.786v-1.572L12.786 0h-1.572zM12 7.714L16.286 12 12 16.286 7.714 12 12 7.714z"/>
                  </svg>
                  <span className="font-medium">Microsoft Teams</span>
                </div>
              </div>
            </div>
            {/* CRM Integrations */}
            <div className="flex items-center justify-center gap-2">
              <span className="text-pb-gray-500 text-sm">Syncs with:</span>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-pb-gray-700">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.943 6.213l-1.227-3.04a.8.8 0 00-.8-.533H13.08a.8.8 0 00-.8.533L11.04 6.213a.8.8 0 00.8 1.067h2.4v8.8h-2.4a.8.8 0 00-.8 1.067l1.24 3.04a.8.8 0 00.8.533h7.84a.8.8 0 00.8-.533l1.227-3.04a.8.8 0 00-.8-1.067h-2.4V7.28h2.4a.8.8 0 00.796-1.067zM9.76 6.213l-1.227-3.04a.8.8 0 00-.8-.533H.093a.8.8 0 00-.8.533L-1.934 6.213a.8.8 0 00.8 1.067h2.4v8.8h-2.4a.8.8 0 00-.8 1.067l1.227 3.04a.8.8 0 00.8.533h7.84a.8.8 0 00.8-.533l1.227-3.04a.8.8 0 00-.8-1.067h-2.4V7.28h2.4a.8.8 0 00.8-1.067z"/>
                  </svg>
                  <span className="font-medium">HubSpot</span>
                </div>
                <div className="flex items-center gap-2 text-pb-gray-700">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm.22 4.668h.003c.347 0 .672.063.978.178l-.647 1.263a1.91 1.91 0 00-.334-.036c-1.07 0-1.944.873-1.944 1.944v.73h2.534v1.457H10.28v6.128H8.56V10.2H6.95V8.747h1.61v-.73c0-2.015 1.645-3.658 3.66-3.658v.309zm4.553 4.08c1.837 0 3.329 1.491 3.329 3.329v.003c0 1.837-1.492 3.326-3.329 3.326-1.84 0-3.332-1.489-3.332-3.326v-.003c0-1.838 1.492-3.33 3.332-3.33zm0 1.457a1.87 1.87 0 00-1.872 1.872v.003a1.87 1.87 0 001.872 1.869 1.867 1.867 0 001.869-1.87v-.002a1.867 1.867 0 00-1.87-1.872z"/>
                  </svg>
                  <span className="font-medium">Salesforce</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={MARKETPLACE_URL}
              className="inline-flex items-center px-6 py-3 bg-pb-orange text-white rounded-lg font-semibold hover:bg-pb-orange-dark hover:shadow-lg transition-all"
            >
              Subscribe on AWS Marketplace
              <ExternalLink className="w-4 h-4 ml-2" />
            </a>
            <Link
              to="/features"
              className="inline-flex items-center px-6 py-3 border border-pb-gray-300 text-pb-gray-700 rounded-lg font-semibold hover:bg-pb-gray-50 hover:shadow-sm transition-all"
            >
              Learn More
            </Link>
          </div>
          <p className="text-sm text-pb-gray-500 mt-4">
            *See AWS Marketplace for full pricing details
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-white" ref={featuresRef}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-display font-bold text-pb-blue text-center mb-12">
            Everything you need to manage ACE opportunities
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <FeatureCard
              icon={<MessageSquare className="w-6 h-6" />}
              title="Natural Language Queries"
              description="Ask about opportunities in plain English. PartnerBuddy understands your questions and finds the right data."
              delay={0}
            />
            <FeatureCard
              icon={<Zap className="w-6 h-6" />}
              title="CRM Integration"
              description="Sync with HubSpot or Salesforce to keep your CRM in sync with AWS Partner Central."
              delay={1}
            />
            <FeatureCard
              icon={<BarChart3 className="w-6 h-6" />}
              title="Automated Reports"
              description="Schedule daily, weekly, or monthly reports delivered right to your team channels."
              delay={2}
            />
            <FeatureCard
              icon={<Shield className="w-6 h-6" />}
              title="Secure by Design"
              description="Cross-account IAM roles with least privilege access. Your data never leaves AWS."
              delay={3}
            />
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 bg-pb-gray-50" ref={stepsRef}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-display font-bold text-pb-blue text-center mb-12">
            Get started in minutes
          </h2>
          <div className="space-y-6">
            <StepCard number={1} title="Subscribe on AWS Marketplace" description="Choose a plan and subscribe through AWS Marketplace. Billing is handled through your AWS account." delay={0} />
            <StepCard number={2} title="Connect Your Workspace" description="Install PartnerBuddy to your Slack workspace or Microsoft Teams tenant and configure which channels can access your data." delay={1} />
            <StepCard number={3} title="Grant AWS Access" description="Deploy a CloudFormation template that creates a secure cross-account role for ACE access." delay={2} />
            <StepCard number={4} title="Start Asking Questions" description="Mention @PartnerBuddy in your channel and ask about your opportunities in natural language." delay={3} />
          </div>
        </div>
      </section>

      {/* AI Disclosure Section */}
      <section className="py-16 px-4 bg-pb-orange/5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-display font-bold text-pb-gray-800 mb-4">
            AI-Powered Assistance
          </h2>
          <p className="text-pb-gray-700 leading-relaxed">
            PartnerBuddy uses artificial intelligence to understand
            your queries and generate helpful responses. While we strive for accuracy, AI-generated
            responses may contain errors. Always verify critical business information through
            the official AWS Partner Central console.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-pb-blue">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-display font-bold text-white mb-4">
            Ready to streamline your partner workflow?
          </h2>
          <p className="text-pb-gray-300 mb-8">
            Join teams using PartnerBuddy to manage their AWS partner opportunities more efficiently.
          </p>
          <a
            href={MARKETPLACE_URL}
            className="inline-flex items-center px-6 py-3 bg-pb-orange text-white rounded-lg font-semibold hover:bg-pb-orange-dark hover:shadow-lg transition-all"
          >
            Get Started on AWS Marketplace
            <ExternalLink className="w-4 h-4 ml-2" />
          </a>
          <p className="text-sm text-pb-gray-400 mt-6">
            Need to share this internally?{' '}
            <a
              href="/partnerbuddy-one-pager.pdf"
              download
              className="text-pb-orange hover:underline"
            >
              Download the one-pager (PDF)
            </a>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function FeatureCard({ icon, title, description, delay }: { icon: React.ReactNode; title: string; description: string; delay: number }) {
  return (
    <div
      className="scroll-reveal opacity-0 bg-pb-gray-50 border border-pb-gray-200 rounded-xl p-6 hover:shadow-rhythmic transition-all"
      style={{ transitionDelay: `${delay * 100}ms` }}
    >
      <div className="bg-pb-orange/10 rounded-lg p-3 w-fit mb-4 text-pb-orange">
        {icon}
      </div>
      <h3 className="font-semibold text-pb-gray-800 mb-2">{title}</h3>
      <p className="text-pb-gray-600 text-sm">{description}</p>
    </div>
  );
}

function StepCard({ number, title, description, delay }: { number: number; title: string; description: string; delay: number }) {
  return (
    <div
      className="scroll-reveal opacity-0 bg-white rounded-xl p-6 flex items-start shadow-sm hover:shadow-rhythmic transition-all"
      style={{ transitionDelay: `${delay * 100}ms` }}
    >
      <div className="bg-pb-orange text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 flex-shrink-0">
        {number}
      </div>
      <div>
        <h3 className="font-semibold text-pb-gray-800 mb-1">{title}</h3>
        <p className="text-pb-gray-600 text-sm">{description}</p>
      </div>
    </div>
  );
}

export default App;
